import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IMG } from "@/lib/assets";
import { FEATURES } from "@/lib/features";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Trophy, Activity, Settings2, Sparkles } from "lucide-react";

export const GOALS = [
  { key: "muscle", label: "Build muscle", img: IMG.strength },
  { key: "strength", label: "Get stronger", img: IMG.strength },
  { key: "fat_loss", label: "Lose fat", img: IMG.conditioning },
  { key: "fitness", label: "Get fitter", img: IMG.conditioning },
  { key: "sport", label: "Sport performance", img: IMG.progress },
  { key: "mobility", label: "Move better", img: IMG.yoga },
];
const FOCUS = ["Strength", "HIIT", "Running", "Yoga", "Mobility", "Cycling", "Swimming", "Padel", "Football"].filter((f) => f !== "Padel" || FEATURES.padel);

export function PrefsForm({ onDone, compact = false }) {
  const { user, refresh } = useAuth();
  const p = user?.prefs || {};
  const [goal, setGoal] = useState(p.goal || "");
  const [level, setLevel] = useState(p.level || "beginner");
  const [days, setDays] = useState(p.days_per_week || 3);
  const [focus, setFocus] = useState(p.focus || []);
  const [injuries, setInjuries] = useState(p.injuries || "");
  const [sport, setSport] = useState(p.sport || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!goal) { toast.error("Pick your main goal first."); return; }
    setBusy(true);
    try {
      await api.patch("/api/me/prefs", { goal, level, days_per_week: days, focus, injuries, sport });
      await refresh();
      toast.success("Saved. Your coach will tailor everything to this.");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div>
        <p className="mono-label mb-2">your main goal</p>
        <div className={`grid ${compact ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 md:grid-cols-3 xl:grid-cols-6"} gap-2`}>
          {GOALS.map((g) => (
            <button key={g.key} type="button" onClick={() => setGoal(g.key)} data-testid={`goal-${g.key}`}
              className={`relative h-24 rounded-xl overflow-hidden text-left border-2 transition-all ${goal === g.key ? "border-acid scale-[1.02]" : "border-transparent"}`}>
              <img src={g.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#050505 15%,rgba(5,5,5,0.2))" }} />
              <span className="absolute bottom-2 left-3 font-heading font-bold">{g.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <p className="mono-label mb-2">experience</p>
          <div className="inline-flex rounded-full border border-[#343A4A] p-1">
            {["beginner", "intermediate", "advanced"].map((l) => (
              <button key={l} type="button" onClick={() => setLevel(l)} className={`px-4 h-8 rounded-full text-sm capitalize ${level === l ? "bg-acid text-black" : "text-[#8A91A6]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="mono-label mb-2">training days a week: <span className="text-acid">{days}</span></p>
          <input type="range" min="1" max="7" value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-full accent-[#CCFF00]" />
        </div>
      </div>
      <div>
        <p className="mono-label mb-2">what you enjoy</p>
        <div className="flex flex-wrap gap-1.5">
          {FOCUS.map((f) => {
            const on = focus.includes(f);
            return <button key={f} type="button" onClick={() => setFocus(on ? focus.filter((x) => x !== f) : [...focus, f])}
              className={`px-3 h-8 rounded-full text-sm border ${on ? "bg-acid text-black border-acid" : "border-[#343A4A] text-[#B7BECB]"}`}>{f}</button>;
          })}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input value={sport} onChange={(e) => setSport(e.target.value)} placeholder="Your sport or event (optional)" className="h-11 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" />
        <input value={injuries} onChange={(e) => setInjuries(e.target.value)} placeholder="Injuries or limits your coach should respect (optional)" className="h-11 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" />
      </div>
      <button onClick={save} disabled={busy} className="h-11 px-6 rounded-full bg-acid text-black font-medium hover:bg-[#AACC00]" data-testid="prefs-save">{busy ? "Saving…" : "Save my profile"}</button>
    </div>
  );
}

export function PersonaliseCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const p = user?.prefs || {};
  if (!user || user.role !== "client") return null;
  if (!p.goal) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="w-full relative overflow-hidden rounded-2xl border border-acid/40 bg-gradient-to-br from-[#12160A] to-[#0B0D11] p-5 md:p-6" data-testid="personalise">
        <div className="absolute -top-24 -right-20 w-80 h-80 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: "#CCFF00" }} />
        <p className="relative mono-label text-acid flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> make OCTON yours</p>
        <h2 className="relative font-heading font-black text-2xl tracking-tight mt-1 mb-4">What are you training for?</h2>
        <div className="relative"><PrefsForm /></div>
      </motion.div>
    );
  }
  const g = GOALS.find((x) => x.key === p.goal);
  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex flex-wrap items-center gap-2 rounded-full border border-[#343A4A] px-4 h-9 text-sm text-[#B7BECB] hover:border-acid" data-testid="prefs-edit">
        <Settings2 className="w-4 h-4 text-acid" /> {g?.label || "Your goal"} · {p.level} · {p.days_per_week} days a week <span className="text-acid">edit</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="surface border-[#1E222A] max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading">Your training profile</DialogTitle></DialogHeader>
          <PrefsForm compact onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ClubBanner() {
  const { user } = useAuth();
  const { data: c } = useQuery({ queryKey: ["club-mine"], queryFn: async () => (await api.get("/api/club/mine")).data, enabled: !!user?.club_id });
  if (!c) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border p-5 md:p-6" style={{ borderColor: `${c.color}66`, background: `linear-gradient(120deg, ${c.color}22, #0B0D11 55%)` }} data-testid="club-banner">
      <div className="absolute -top-20 -left-16 w-64 h-64 rounded-full blur-3xl opacity-25 pointer-events-none" style={{ background: c.color }} />
      <div className="relative flex flex-col md:flex-row md:items-center gap-4">
        <span className="w-14 h-14 rounded-2xl flex items-center justify-center font-heading font-black text-2xl text-black shrink-0" style={{ background: c.color }}>
          {c.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
        </span>
        <div className="flex-1">
          <p className="mono-label" style={{ color: c.color }}>your club</p>
          <p className="font-heading font-black text-2xl tracking-tight">{c.name}</p>
          {c.welcome ? <p className="text-sm text-[#D7DCE5] mt-1 italic">"{c.welcome}"{c.coach ? <span className="not-italic text-[#8A91A6]"> · {c.coach}</span> : null}</p>
            : <p className="text-sm text-[#8A91A6] mt-1">Train hard, log it, and help the squad climb the leaderboard.</p>}
          {!c.active && <p className="text-xs text-[#FFB340] mt-1">The club's plan isn't active right now. Ask your coach.</p>}
        </div>
        <div className="flex gap-4">
          <Stat icon={Users} label="squad" value={c.squad_size} />
          <Stat icon={Activity} label="trained this week" value={c.trained_this_week} />
          <Stat icon={Trophy} label="PRs this week" value={c.prs_this_week} />
        </div>
      </div>
    </motion.div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <Icon className="w-4 h-4 mx-auto text-[#8A91A6]" />
      <p className="font-heading font-black text-2xl">{value}</p>
      <p className="mono-label">{label}</p>
    </div>
  );
}
